import * as SMS from 'expo-sms';
import { Platform } from 'react-native';

import { normalizeUsPhone } from '@/lib/contact';

export type SmsResult = 'sent' | 'cancelled' | 'unknown' | 'unavailable';

export interface SendOneInput {
  phoneE164: string;
  body: string;
}

export interface SendOneOutput {
  result: SmsResult;
}

/**
 * Wrapper around expo-sms. Treats any return as "user finished with
 * this recipient" because iOS doesn't reliably distinguish Send vs
 * Cancel after the composer opens — the result is virtually always
 * `'unknown'` on iOS.
 *
 * Web has no expo-sms support; returns 'unavailable' so callers can
 * show the right empty state. The phoneE164 input is validated with
 * `normalizeUsPhone` as defense-in-depth: the UI is responsible for
 * filtering email-only recipient_contact values out of the textable
 * subset, but the service refuses non-phone inputs in case the filter
 * is bypassed (would otherwise hand an email to iOS Messages).
 *
 * Multi-recipient sends are always done by looping `sendOne` from the
 * caller (see `app/referrals/nudge.tsx`, the only caller).
 * The previous `sendMany` shape (one group iMessage to all phones) was
 * removed because it leaked phone numbers between strangers and broke
 * per-recipient `{{first_name}}` resolution.
 */

export const smsSenderService = {
  async sendOne({ phoneE164, body }: SendOneInput): Promise<SendOneOutput> {
    if (Platform.OS === 'web') {
      return { result: 'unavailable' };
    }
    if (!normalizeUsPhone(phoneE164)) {
      throw new Error(`smsSenderService.sendOne: not a valid phone (${phoneE164})`);
    }

    const isAvailable = await SMS.isAvailableAsync();
    if (!isAvailable) {
      return { result: 'unavailable' };
    }

    const { result } = await SMS.sendSMSAsync([phoneE164], body);
    // SMSResponse.result is 'sent' | 'cancelled' | 'unknown'. iOS almost
    // always returns 'unknown'; we don't branch on the value because
    // we cannot trust it — the caller advances regardless.
    return { result };
  },
};
